import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import api from '../services/api';
import { fmtDateTime } from '../utils/helpers';
import {useTranslation} from 'react-i18next';

const RESTORE_PHRASE = 'RESTORE DATABASE';

const STATUS_CSS: Record<string, string> = {
  SUCCESS: 'bg-green-100 text-green-700', RUNNING: 'bg-blue-100 text-blue-700', FAILED: 'bg-red-100 text-red-700',
};
const DR_STATUS_CSS: Record<string, string> = { READY: 'bg-green-100 text-green-700', AT_RISK: 'bg-amber-100 text-amber-700', NO_BACKUP: 'bg-red-100 text-red-700' };
const DR_STATUS_LABEL: Record<string, string> = { READY: 'Within RPO target', AT_RISK: 'Behind RPO target', NO_BACKUP: 'No successful backup yet' };

function fmtBytes(n?: number | string) {
  const v = +(n || 0);
  if (!v) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0, val = v;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(1)} ${units[i]}`;
}

export default function BackupPage() {
  const {t}=useTranslation();
  const [backups, setBackups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [msg, setMsg] = useState('');
  const [restoreTarget, setRestoreTarget] = useState<any>(null);
  const [restoreInput, setRestoreInput] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [dr, setDr] = useState<any>(null);

  const notify = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const load = useCallback(() => {
    setLoading(true);
    api.getBackups({ take: 50 }).then(r => setBackups(r.data?.data || [])).catch(() => notify('Failed to load backups')).finally(() => setLoading(false));
    api.getDrStatus().then(r => setDr(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const trigger = async () => {
    setTriggering(true);
    try {
      const r = await api.triggerBackup();
      notify(r.data?.status === 'SUCCESS' ? 'Backup completed successfully' : `Backup failed: ${r.data?.errorMessage || 'unknown error'}`);
      load();
    } catch (e: any) {
      notify(e.response?.data?.message || 'Failed to trigger backup');
    } finally {
      setTriggering(false);
    }
  };

  const download = async (b: any) => {
    try {
      const r = await api.downloadBackup(b.id);
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = b.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      notify('Failed to download backup — file may no longer exist on disk');
    }
  };

  const remove = async (b: any) => {
    if (!window.confirm(`Delete backup "${b.filename}"? This cannot be undone.`)) return;
    try { await api.deleteBackup(b.id); notify('Backup deleted'); load(); } catch { notify('Failed to delete backup'); }
  };

  const doRestore = async () => {
    if (restoreInput !== RESTORE_PHRASE) return;
    setRestoring(true);
    try {
      await api.restoreBackup(restoreTarget.id, restoreInput);
      notify('Database restored successfully');
      setRestoreTarget(null);
      setRestoreInput('');
    } catch (e: any) {
      notify(e.response?.data?.message || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Layout title="Backup & Restore" actions={
      <button onClick={trigger} disabled={triggering} className="px-4 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #02357A, #015CA6)' }}>
        {triggering ? 'Backing up…' : '+ Backup Now'}
      </button>
    }>
      <div className="max-w-5xl mx-auto">
        {msg && <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl text-sm">{msg}</div>}

        {dr && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-gray-700">Disaster Recovery Readiness</h2>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${DR_STATUS_CSS[dr.status]}`}>{DR_STATUS_LABEL[dr.status]}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Last backup age</p>
                <p className="text-sm font-semibold text-gray-700">{dr.lastBackupAgeHours !== null ? `${dr.lastBackupAgeHours}h` : 'Never'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">RPO target</p>
                <p className="text-sm font-semibold text-gray-700">{dr.rpoTargetHours}h</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">RTO target</p>
                <p className="text-sm font-semibold text-gray-700">{dr.rtoTargetHours}h</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Recent failed backups</p>
                <p className="text-sm font-semibold text-gray-700">{dr.recentFailedBackupCount}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs font-semibold text-slate-600 mb-1">Off-site backup copy — Not configured</p>
                <p className="text-xs text-slate-500">{dr.offsite?.note}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs font-semibold text-slate-600 mb-1">Standby replica / failover — Not configured</p>
                <p className="text-xs text-slate-500">{dr.replication?.note}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          Nightly backups run automatically at 02:00. Backups older than 30 days are removed automatically. Restoring a backup <strong>overwrites the current database</strong> — this cannot be undone.
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400 text-sm">{t('common.loading','Loading…')}</div>
        ) : backups.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm bg-white rounded-2xl border border-gray-100">No backups yet. Click "Backup Now" to create one.</div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Filename</th>
                  <th className="text-left px-4 py-2.5">{t('common.type','Type')}</th>
                  <th className="text-left px-4 py-2.5">{t('common.status','Status')}</th>
                  <th className="text-left px-4 py-2.5">Size</th>
                  <th className="text-left px-4 py-2.5">Started</th>
                  <th className="text-right px-4 py-2.5">{t('common.actions','Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {backups.map(b => (
                  <tr key={b.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-medium text-gray-700 truncate max-w-xs">{b.filename}</td>
                    <td className="px-4 py-2.5 text-gray-500">{b.backupType}</td>
                    <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_CSS[b.status] || 'bg-slate-100 text-slate-500'}`}>{b.status}</span></td>
                    <td className="px-4 py-2.5 text-gray-500">{fmtBytes(b.fileSizeBytes)}</td>
                    <td className="px-4 py-2.5 text-gray-500">{fmtDateTime(b.startedAt)}</td>
                    <td className="px-4 py-2.5 text-right space-x-2 whitespace-nowrap">
                      {b.status === 'SUCCESS' && <>
                        <button onClick={() => download(b)} className="text-xs font-semibold text-[#02357A] hover:underline">{t('common.download','Download')}</button>
                        <button onClick={() => setRestoreTarget(b)} className="text-xs font-semibold text-amber-600 hover:underline">Restore</button>
                      </>}
                      <button onClick={() => remove(b)} className="text-xs font-semibold text-red-500 hover:underline">{t('common.delete','Delete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {restoreTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setRestoreTarget(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Restore Database</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will <strong>overwrite the entire current database</strong> with the contents of <code className="bg-slate-100 px-1 rounded">{restoreTarget.filename}</code>. This action cannot be undone.
            </p>
            <p className="text-xs text-gray-500 mb-1.5">Type <strong>{RESTORE_PHRASE}</strong> to confirm:</p>
            <input value={restoreInput} onChange={e => setRestoreInput(e.target.value)} autoFocus
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none mb-4" placeholder={RESTORE_PHRASE} />
            <div className="flex gap-2">
              <button onClick={() => { setRestoreTarget(null); setRestoreInput(''); }} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm">{t('common.cancel','Cancel')}</button>
              <button onClick={doRestore} disabled={restoreInput !== RESTORE_PHRASE || restoring}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                {restoring ? 'Restoring…' : 'Restore & Overwrite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
